import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Package,
  ShoppingCart,
  Layers,
  TrendingUp,
  RefreshCw,
  Plus,
  Minus,
  Info,
} from "lucide-react";
import * as FIFOService from "@/services/fifo-service";
import { CostLayer } from "@/types/cost-layer";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ValuationMethodComparison } from "@/components/valuation-method-comparison";

export function FIFODemoPage() {
  const [stockCode] = useState("DEMO-001");
  const [warehouseCode] = useState("WH01");
  const [costLayers, setCostLayers] = useState<CostLayer[]>([]);
  const [summary, setSummary] = useState<any>(null);

  // Purchase form
  const [purchaseQty, setPurchaseQty] = useState(100);
  const [purchaseCost, setPurchaseCost] = useState(10);
  const [purchaseRef, setPurchaseRef] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  // Sale form
  const [saleQty, setSaleQty] = useState(50);
  const [saleResult, setSaleResult] = useState<any>(null);

  // Load cost layers
  const loadData = () => {
    const layers = FIFOService.getCostLayers(stockCode, warehouseCode);
    setCostLayers(layers);
    
    const summaryData = FIFOService.getCostLayerSummary(stockCode, warehouseCode);
    setSummary(summaryData);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handlePurchase = () => {
    if (purchaseQty <= 0 || purchaseCost <= 0) {
      toast.error("Please enter valid quantity and cost");
      return;
    }

    FIFOService.addCostLayer(
      stockCode,
      warehouseCode,
      "A1",
      purchaseQty,
      purchaseCost,
      purchaseRef || `PO-${Date.now()}`,
      purchaseDate
    );

    toast.success(`Added ${purchaseQty} units at £${purchaseCost.toFixed(2)} each`);
    loadData();
    
    // Auto-increment reference
    const match = purchaseRef.match(/PO-(\d+)/);
    if (match) {
      const num = parseInt(match[1]) + 1;
      setPurchaseRef(`PO-${num.toString().padStart(3, '0')}`);
    } else {
      setPurchaseRef("PO-001");
    }
  };

  const handleSale = () => {
    if (saleQty <= 0) {
      toast.error("Please enter valid quantity");
      return;
    }

    if (!summary || saleQty > summary.totalQuantity) {
      toast.error(`Insufficient stock. Available: ${summary?.totalQuantity || 0}`);
      return;
    }

    const result = FIFOService.calculateFIFOCost(stockCode, warehouseCode, saleQty);
    setSaleResult(result);

    toast.success(
      `Sold ${saleQty} units. COGS: £${result.totalCost.toFixed(2)} (Avg: £${result.averageCost.toFixed(2)}/unit)`
    );
    loadData();
  };

  const handleReset = () => {
    FIFOService.clearCostLayers(stockCode, warehouseCode);
    setSaleResult(null);
    toast.info("Cost layers cleared");
    loadData();
  };

  const handleInitializeDemo = () => {
    FIFOService.clearCostLayers(stockCode, warehouseCode);
    
    // Add demo layers with increasing costs and specific dates
    FIFOService.addCostLayer(stockCode, warehouseCode, "A1", 100, 10.00, "PO-001", "2024-01-15");
    FIFOService.addCostLayer(stockCode, warehouseCode, "A1", 75, 11.50, "PO-002", "2024-02-20");
    FIFOService.addCostLayer(stockCode, warehouseCode, "A1", 50, 12.00, "PO-003", "2024-03-10");
    
    toast.success("Demo data initialized with 3 cost layers");
    setPurchaseRef("PO-004");
    setSaleResult(null);
    loadData();
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold tracking-tight">FIFO Valuation Demo</h1>
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            🟡 Demo Mode
          </Badge>
          <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-300">
            ✓ Implemented
          </Badge>
        </div>
        <p className="text-muted-foreground mt-2">
          Interactive demonstration of FIFO (First In, First Out) inventory valuation method.
        </p>
      </div>

      <Tabs defaultValue="interactive" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="interactive">Interactive Demo</TabsTrigger>
          <TabsTrigger value="comparison">Method Comparison</TabsTrigger>
        </TabsList>

        <TabsContent value="interactive" className="space-y-6 mt-6">

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Quantity</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalQuantity || 0}</div>
            <p className="text-xs text-muted-foreground">Units in stock</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">£{summary?.totalValue.toFixed(2) || "0.00"}</div>
            <p className="text-xs text-muted-foreground">Inventory value</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Weighted Avg Cost</CardTitle>
            <Layers className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              £{summary?.weightedAverageCost.toFixed(2) || "0.00"}
            </div>
            <p className="text-xs text-muted-foreground">Per unit</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cost Layers</CardTitle>
            <Layers className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.layerCount || 0}</div>
            <p className="text-xs text-muted-foreground">Active layers</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Purchase Form */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Receive Stock (Purchase)
                </CardTitle>
                <CardDescription>Add a new cost layer</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Purchase Date</Label>
              <Input
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input
                type="number"
                value={purchaseQty}
                onChange={(e) => setPurchaseQty(parseInt(e.target.value) || 0)}
                min="1"
              />
            </div>

            <div className="space-y-2">
              <Label>Unit Cost (£)</Label>
              <Input
                type="number"
                step="0.01"
                value={purchaseCost}
                onChange={(e) => setPurchaseCost(parseFloat(e.target.value) || 0)}
                min="0.01"
              />
            </div>

            <div className="space-y-2">
              <Label>Reference (PO Number)</Label>
              <Input
                value={purchaseRef}
                onChange={(e) => setPurchaseRef(e.target.value)}
                placeholder="PO-001"
              />
            </div>

            <Button onClick={handlePurchase} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Add Purchase
            </Button>
          </CardContent>
        </Card>

        {/* Sale Form */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  Sell Stock (FIFO)
                </CardTitle>
                <CardDescription>Calculate COGS using FIFO method</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Quantity to Sell</Label>
              <Input
                type="number"
                value={saleQty}
                onChange={(e) => setSaleQty(parseInt(e.target.value) || 0)}
                min="1"
              />
              <p className="text-xs text-muted-foreground">
                Available: {summary?.totalQuantity || 0} units
              </p>
            </div>

            <Button onClick={handleSale} className="w-full" variant="secondary">
              <Minus className="h-4 w-4 mr-2" />
              Calculate FIFO Sale
            </Button>

            {saleResult && (
              <div className="p-4 border rounded-lg bg-muted/50 space-y-2">
                <div className="font-semibold">Sale Result:</div>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span>Quantity Sold:</span>
                    <span className="font-medium">{saleQty} units</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total COGS:</span>
                    <span className="font-medium">£{saleResult.totalCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Avg Cost/Unit:</span>
                    <span className="font-medium">£{saleResult.averageCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Layers Used:</span>
                    <span className="font-medium">{saleResult.layersUsed.length}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cost Layers Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5" />
                Cost Layers
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        FIFO consumes from the oldest layer first. Each purchase creates a new layer.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
              <CardDescription>
                Showing {costLayers.length} active cost layer(s) - oldest first
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleInitializeDemo}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Initialize Demo
              </Button>
              <Button variant="outline" size="sm" onClick={handleReset}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Clear All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {costLayers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No cost layers yet</p>
              <p className="text-sm">Add a purchase to create your first cost layer</p>
              <Button onClick={handleInitializeDemo} className="mt-4">
                Initialize Demo Data
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Purchase Date</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Original Qty</TableHead>
                  <TableHead className="text-right">Remaining Qty</TableHead>
                  <TableHead className="text-right">Unit Cost</TableHead>
                  <TableHead className="text-right">Layer Value</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costLayers.map((layer, index) => {
                  const layerValue = layer.remainingQuantity * layer.unitCost;
                  const consumed = layer.quantity - layer.remainingQuantity;
                  const consumedPercent = (consumed / layer.quantity) * 100;

                  return (
                    <TableRow key={layer.id}>
                      <TableCell className="font-medium">
                        {formatDate(layer.purchaseDate)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{layer.reference || "N/A"}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{layer.quantity}</TableCell>
                      <TableCell className="text-right font-medium">
                        {layer.remainingQuantity}
                      </TableCell>
                      <TableCell className="text-right">£{layer.unitCost.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-medium">
                        £{layerValue.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {index === 0 && layer.remainingQuantity > 0 ? (
                          <Badge className="bg-green-100 text-green-700 border-green-300">
                            Next to Consume
                          </Badge>
                        ) : consumed > 0 ? (
                          <Badge variant="outline">
                            {consumedPercent.toFixed(0)}% Used
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Waiting</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Last Sale Details */}
      {saleResult && saleResult.layersUsed.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Last Sale - Layer Breakdown</CardTitle>
            <CardDescription>
              Shows which cost layers were consumed in the last sale
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Layer</TableHead>
                  <TableHead className="text-right">Qty Used</TableHead>
                  <TableHead className="text-right">Unit Cost</TableHead>
                  <TableHead className="text-right">Total Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {saleResult.layersUsed.map((transaction: any, index: number) => (
                  <TableRow key={transaction.layerId}>
                    <TableCell>
                      <Badge variant="outline">Layer {index + 1}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{transaction.quantityUsed}</TableCell>
                    <TableCell className="text-right">£{transaction.unitCost.toFixed(2)}</TableCell>
                    <TableCell className="text-right font-medium">
                      £{transaction.totalCost.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/50 font-semibold">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right">{saleQty}</TableCell>
                  <TableCell className="text-right">
                    £{saleResult.averageCost.toFixed(2)} avg
                  </TableCell>
                  <TableCell className="text-right">£{saleResult.totalCost.toFixed(2)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* How FIFO Works */}
      <Card>
        <CardHeader>
          <CardTitle>How FIFO Works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-semibold">First In, First Out (FIFO)</h4>
            <p className="text-sm text-muted-foreground">
              FIFO assumes that the oldest inventory items are sold first. This method:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1 ml-4">
              <li>Tracks each purchase as a separate "cost layer"</li>
              <li>Consumes from the oldest layer first when selling</li>
              <li>Matches the physical flow of goods (especially for perishables)</li>
              <li>Results in lower COGS during inflation (older, cheaper costs used first)</li>
              <li>Provides more accurate inventory valuation at current costs</li>
            </ul>
          </div>

          <Separator />

          <div className="space-y-2">
            <h4 className="font-semibold">Example Scenario</h4>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>1. Purchase 100 units at £10 each (Layer 1)</p>
              <p>2. Purchase 75 units at £11.50 each (Layer 2)</p>
              <p>3. Sell 120 units:</p>
              <ul className="list-disc list-inside ml-4">
                <li>First 100 units from Layer 1 at £10 = £1,000</li>
                <li>Next 20 units from Layer 2 at £11.50 = £230</li>
                <li>Total COGS = £1,230 (Average: £10.25/unit)</li>
              </ul>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <h4 className="font-semibold">Best For</h4>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">Perishable Goods</Badge>
              <Badge variant="outline">Fashion & Apparel</Badge>
              <Badge variant="outline">Electronics</Badge>
              <Badge variant="outline">Food & Beverage</Badge>
              <Badge variant="outline">Pharmaceuticals</Badge>
              <Badge variant="outline">Time-Sensitive Products</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="comparison" className="space-y-6 mt-6">
          <ValuationMethodComparison />
        </TabsContent>
      </Tabs>
    </div>
  );
}
